import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useAuth } from "@/lib/auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Sparkles, Send, CheckCircle2, XCircle, Lightbulb, Lock, Wand2, Bot, Paperclip, X, FileText } from "lucide-react";
import { Link } from "wouter";
import { useToast } from "@/hooks/use-toast";

type SetupAgentInfo = {
  available: boolean;
  currentPlan: string;
  planRequired: string;
  greeting: string;
  tagline: string;
  suggestions: string[];
  supportedActions: string[];
};

type ProposedAction = { tool: string; summary: string; params: Record<string, unknown> };
type Attachment = { name: string; mimeType: string; data: string; size: number; previewUrl?: string };
type Turn =
  | { role: "user"; content: string; attachments?: { name: string; mimeType: string }[] }
  | { role: "assistant"; content: string; action?: ProposedAction; actionState?: "pending" | "approved" | "rejected" | "running" | "error"; resultSummary?: string };

const MAX_ATTACHMENTS = 6;
const MAX_ATTACHMENT_BYTES = 12 * 1024 * 1024;
const MAX_MESSAGE_CHARS = 100_000;

// apiRequest throws `Error("<status>: <body>")`. Pull the server's real message
// (plain `{message}` or ASP.NET problem-details `{errors|title|detail}`) out of it.
function serverErrorMessage(e: any): string | null {
  const raw = typeof e?.message === "string" ? e.message : "";
  const idx = raw.indexOf(": ");
  const body = idx >= 0 ? raw.slice(idx + 2) : raw;
  try {
    const j = JSON.parse(body);
    if (typeof j?.message === "string" && j.message.trim()) return j.message.trim();
    if (j?.errors && typeof j.errors === "object") {
      const msgs = Object.values(j.errors).flat().filter((m): m is string => typeof m === "string" && !!m.trim());
      if (msgs.length) return msgs.join(" ");
    }
    if (typeof j?.detail === "string" && j.detail.trim()) return j.detail.trim();
    if (typeof j?.title === "string" && j.title.trim()) return j.title.trim();
  } catch {}
  return null;
}

function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const res = r.result as string;
      const comma = res.indexOf(",");
      resolve(comma >= 0 ? res.slice(comma + 1) : res);
    };
    r.onerror = () => reject(new Error("read failed"));
    r.readAsDataURL(file);
  });
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function ActionBadge({ tool }: { tool: string }) {
  const label = tool.replace(/_/g, " ");
  return (
    <Badge variant="secondary" className="text-[10px] uppercase tracking-wider">
      <Wand2 className="w-3 h-3 mr-1" />
      {label}
    </Badge>
  );
}

function ParamList({ params }: { params: Record<string, unknown> }) {
  const entries = Object.entries(params);
  if (entries.length === 0) return null;
  return (
    <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
      {entries.map(([k, v]) => (
        <div key={k} className="contents">
          <dt className="text-muted-foreground font-medium">{k}</dt>
          <dd className="font-mono break-words" data-testid={`param-${k}`}>
            {typeof v === "string" ? v.length > 200 ? v.slice(0, 200) + "…" : v : JSON.stringify(v)}
          </dd>
        </div>
      ))}
    </dl>
  );
}

export default function SetupAgentPage() {
  const { organization } = useAuth();
  const { toast } = useToast();
  const { data: info, isLoading } = useQuery<SetupAgentInfo>({ queryKey: ["/api/setup-agent/info"] });
  const [history, setHistory] = useState<Turn[]>([]);
  const [input, setInput] = useState("");
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [pending, setPending] = useState(false);
  const scroller = useRef<HTMLDivElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const promptBox = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const el = promptBox.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, [input]);

  async function onPickFiles(list: FileList | null) {
    if (!list || list.length === 0) return;
    const room = MAX_ATTACHMENTS - attachments.length;
    if (room <= 0) {
      toast({ title: "Attachment limit", description: `You can attach up to ${MAX_ATTACHMENTS} files.`, variant: "destructive" });
      return;
    }
    const picked = Array.from(list).slice(0, room);
    const accepted: Attachment[] = [];
    for (const f of picked) {
      if (f.size > MAX_ATTACHMENT_BYTES) {
        toast({ title: "File too large", description: `${f.name} is over 12 MB.`, variant: "destructive" });
        continue;
      }
      try {
        const data = await readAsBase64(f);
        const mimeType = f.type || "application/octet-stream";
        accepted.push({
          name: f.name,
          mimeType,
          data,
          size: f.size,
          previewUrl: mimeType.startsWith("image/") ? `data:${mimeType};base64,${data}` : undefined,
        });
      } catch {
        toast({ title: "Couldn't read file", description: f.name, variant: "destructive" });
      }
    }
    if (accepted.length) setAttachments(a => [...a, ...accepted]);
  }

  useEffect(() => {
    if (info && history.length === 0)
      setHistory([{ role: "assistant", content: info.greeting }]);
  }, [info, history.length]);

  useEffect(() => {
    scroller.current?.scrollTo({ top: scroller.current.scrollHeight, behavior: "smooth" });
  }, [history, pending]);

  async function send(text: string) {
    const trimmed = text.trim();
    if ((!trimmed && attachments.length === 0) || pending || !info?.available) return;
    if (trimmed.length > MAX_MESSAGE_CHARS) {
      toast({
        title: "Message is too long",
        description: `Keep it under ${MAX_MESSAGE_CHARS.toLocaleString()} characters, or attach it as a file instead.`,
        variant: "destructive",
      });
      return;
    }
    const atts = attachments;
    const userTurn: Turn = {
      role: "user",
      content: trimmed,
      attachments: atts.length ? atts.map(a => ({ name: a.name, mimeType: a.mimeType })) : undefined,
    };
    const next: Turn[] = [...history, userTurn];
    setHistory(next);
    setInput("");
    setAttachments([]);
    setPending(true);
    try {
      const apiHistory = history.filter(t => t.role === "user" || t.role === "assistant").slice(-10).map(t => ({ role: t.role, content: t.content }));
      const res = await apiRequest("POST", "/api/setup-agent/chat", {
        message: trimmed,
        history: apiHistory,
        attachments: atts.map(a => ({ name: a.name, mimeType: a.mimeType, data: a.data })),
      });
      const data = (await res.json()) as { reply: string; action?: ProposedAction | null };
      setHistory([
        ...next,
        { role: "assistant", content: data.reply, action: data.action ?? undefined, actionState: data.action ? "pending" : undefined },
      ]);
    } catch (e: any) {
      const msg = serverErrorMessage(e) || "Sorry, I hit an error. Please try again.";
      setHistory([...next, { role: "assistant", content: msg }]);
    } finally {
      setPending(false);
    }
  }

  async function decide(idx: number, approve: boolean) {
    const turn = history[idx];
    if (turn.role !== "assistant" || !turn.action) return;
    if (turn.actionState !== "pending") return;
    if (!approve) {
      setHistory(h => h.map((t, i) => i === idx ? { ...t, actionState: "rejected" } : t));
      return;
    }
    setHistory(h => h.map((t, i) => i === idx ? { ...t, actionState: "running" } : t));
    try {
      const res = await apiRequest("POST", "/api/setup-agent/execute", { tool: turn.action.tool, params: turn.action.params });
      const data = (await res.json()) as { ok: boolean; summary: string };
      setHistory(h => h.map((t, i) => i === idx ? { ...t, actionState: "approved", resultSummary: data.summary } : t));
      toast({ title: "Done", description: data.summary });
      queryClient.invalidateQueries({ queryKey: ["/api/chatbots"] });
      queryClient.invalidateQueries({ queryKey: ["/api/me/chatbots"] });
      queryClient.invalidateQueries({ queryKey: ["/api/branding"] });
    } catch (e: any) {
      const msg = e?.message || "Action failed.";
      setHistory(h => h.map((t, i) => i === idx ? { ...t, actionState: "error", resultSummary: msg } : t));
      toast({ title: "Action failed", description: msg, variant: "destructive" });
    }
  }

  if (isLoading) {
    return <div className="p-6 mx-auto"><Skeleton className="h-96 w-full" /></div>;
  }

  if (info && !info.available) {
    return (
      <div className="p-6 mx-auto" data-testid="setup-agent-locked">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <span className="grid place-items-center w-12 h-12 rounded-2xl bg-amber-500/15 text-amber-600 dark:text-amber-400">
                <Lock className="w-6 h-6" />
              </span>
              <div>
                <CardTitle className="text-2xl">Setup Agent</CardTitle>
                <p className="text-sm text-muted-foreground mt-1">{info.tagline}</p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert>
              <AlertTitle>Restricted to tenant owners and admins</AlertTitle>
              <AlertDescription>
                The Setup Agent makes changes inside <strong>{organization?.name}</strong> — creating chatbots,
                ingesting knowledge and adjusting branding. Ask your organisation owner or an admin to
                run it for you, or sign in with an admin account.
              </AlertDescription>
            </Alert>
            <div className="grid sm:grid-cols-2 gap-3">
              {info.supportedActions.map(a => (
                <div key={a} className="flex items-center gap-2 p-3 rounded-md border bg-muted/30">
                  <Wand2 className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm font-medium">{a.replace(/_/g, " ")}</span>
                </div>
              ))}
            </div>
            <Link href="/dashboard">
              <Button data-testid="button-back-dashboard">Back to dashboard</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 mx-auto space-y-4" data-testid="setup-agent-page">
      <Card>
        <CardHeader>
          <div className="flex items-start gap-3">
            <span className="grid place-items-center w-12 h-12 rounded-2xl bg-primary text-primary-foreground shrink-0">
              <Sparkles className="w-6 h-6" />
            </span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <CardTitle className="text-2xl">Setup Agent</CardTitle>
                <Badge>Enterprise</Badge>
              </div>
              <p className="text-sm text-muted-foreground mt-1">{info?.tagline}</p>
            </div>
          </div>
        </CardHeader>
      </Card>

      <Card className="flex flex-col h-[min(70vh,640px)]">
        <div ref={scroller} className="flex-1 overflow-auto p-4 space-y-3" data-testid="setup-agent-scroller">
          {history.map((t, i) => {
            if (t.role === "user") {
              return (
                <div key={i} className="ml-auto max-w-[85%] flex flex-col items-end gap-1" data-testid={`bubble-user-${i}`}>
                  {t.content && (
                    <div className="rounded-2xl rounded-tr-sm bg-primary text-primary-foreground px-4 py-2 text-sm whitespace-pre-wrap">
                      {t.content}
                    </div>
                  )}
                  {t.attachments && t.attachments.length > 0 && (
                    <div className="flex flex-wrap gap-1 justify-end">
                      {t.attachments.map((a, j) => (
                        <span
                          key={j}
                          className="flex items-center gap-1 rounded-md bg-primary/10 text-primary px-2 py-0.5 text-[11px] max-w-[200px]"
                          data-testid={`user-attachment-${i}-${j}`}
                        >
                          <Paperclip className="w-3 h-3 shrink-0" />
                          <span className="truncate">{a.name}</span>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            }
            return (
              <div key={i} className="mr-auto max-w-[92%] space-y-2" data-testid={`bubble-assistant-${i}`}>
                <div className="rounded-2xl rounded-tl-sm bg-muted px-4 py-2 text-sm whitespace-pre-wrap">{t.content}</div>
                {t.action && (
                  <div className="rounded-xl border bg-card p-3 shadow-sm" data-testid={`action-card-${i}`}>
                    <div className="flex items-start gap-2 flex-wrap">
                      <ActionBadge tool={t.action.tool} />
                      <p className="text-sm font-medium flex-1 min-w-0">{t.action.summary}</p>
                    </div>
                    <ParamList params={t.action.params} />
                    <div className="mt-3 flex items-center gap-2">
                      {t.actionState === "pending" && (
                        <>
                          <Button size="sm" onClick={() => decide(i, true)} data-testid={`button-approve-${i}`}>
                            <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => decide(i, false)} data-testid={`button-reject-${i}`}>
                            <XCircle className="w-4 h-4 mr-1" /> Reject
                          </Button>
                        </>
                      )}
                      {t.actionState === "running" && <span className="text-xs text-muted-foreground">Running…</span>}
                      {t.actionState === "approved" && (
                        <span className="text-xs text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                          <CheckCircle2 className="w-3.5 h-3.5" /> {t.resultSummary || "Done."}
                        </span>
                      )}
                      {t.actionState === "rejected" && (
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <XCircle className="w-3.5 h-3.5" /> Rejected — nothing was changed.
                        </span>
                      )}
                      {t.actionState === "error" && (
                        <span className="text-xs text-rose-600 dark:text-rose-400 flex items-center gap-1">
                          <XCircle className="w-3.5 h-3.5" /> {t.resultSummary}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {pending && (
            <div className="mr-auto max-w-[60%] rounded-2xl rounded-tl-sm bg-muted px-4 py-2 text-sm flex gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/60 animate-bounce" />
              <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/60 animate-bounce [animation-delay:120ms]" />
              <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/60 animate-bounce [animation-delay:240ms]" />
            </div>
          )}
          {history.length <= 1 && info && info.suggestions.length > 0 && (
            <div className="pt-2">
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground/70 flex items-center gap-1 mb-2">
                <Lightbulb className="w-3 h-3" /> Try asking
              </p>
              <div className="flex flex-wrap gap-2">
                {info.suggestions.map(s => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => send(s)}
                    disabled={pending}
                    className="text-xs px-3 py-1.5 rounded-full border bg-background hover:bg-muted transition-colors disabled:opacity-50"
                    data-testid={`suggestion-${s.slice(0, 24).replace(/\W+/g, "-")}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <form
          onSubmit={(e) => { e.preventDefault(); send(input); }}
          className="border-t p-3 space-y-2"
        >
          {attachments.length > 0 && (
            <div className="flex flex-wrap gap-2" data-testid="attachment-list">
              {attachments.map((a, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 rounded-lg border bg-muted/40 pl-1 pr-2 py-1 text-xs"
                  data-testid={`attachment-chip-${i}`}
                >
                  {a.previewUrl ? (
                    <img src={a.previewUrl} alt={a.name} className="w-8 h-8 rounded object-cover" />
                  ) : (
                    <span className="w-8 h-8 rounded bg-muted grid place-items-center shrink-0">
                      <FileText className="w-4 h-4" />
                    </span>
                  )}
                  <span className="max-w-[140px] truncate" title={a.name}>{a.name}</span>
                  <span className="text-muted-foreground/60">{formatBytes(a.size)}</span>
                  <button
                    type="button"
                    onClick={() => setAttachments(list => list.filter((_, j) => j !== i))}
                    className="text-muted-foreground hover:text-foreground"
                    aria-label={`Remove ${a.name}`}
                    data-testid={`button-remove-attachment-${i}`}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="flex items-end gap-2">
            <input
              ref={fileInput}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => { onPickFiles(e.target.files); e.currentTarget.value = ""; }}
              data-testid="input-attachment-file"
            />
            <IconButton
              label="Attach files"
              variant="outline"
              disabled={pending || attachments.length >= MAX_ATTACHMENTS}
              onClick={() => fileInput.current?.click()}
              data-testid="button-attach"
            >
              <Paperclip className="w-4 h-4" aria-hidden="true" />
            </IconButton>
            <Textarea
              ref={promptBox}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== "Enter" || e.nativeEvent.isComposing) return;
                if (e.shiftKey) return; // native newline insertion
                if (e.ctrlKey || e.metaKey) {
                  e.preventDefault();
                  const ta = e.currentTarget;
                  const start = ta.selectionStart;
                  const end = ta.selectionEnd;
                  setInput(input.slice(0, start) + "\n" + input.slice(end));
                  requestAnimationFrame(() => {
                    const el = promptBox.current;
                    if (el) el.selectionStart = el.selectionEnd = start + 1;
                  });
                  return;
                }
                e.preventDefault();
                send(input);
              }}
              onPaste={(e) => {
                const text = e.clipboardData.getData("text");
                if (!text.includes("\n")) return; // let native handle single-line paste
                e.preventDefault();
                const ta = e.currentTarget;
                const start = ta.selectionStart;
                const end = ta.selectionEnd;
                setInput(input.slice(0, start) + text + input.slice(end));
                requestAnimationFrame(() => {
                  const el = promptBox.current;
                  if (el) el.selectionStart = el.selectionEnd = start + text.length;
                });
              }}
              placeholder="Tell me what to set up…  (Shift+Enter or Ctrl+Enter for a new line)"
              disabled={pending}
              rows={1}
              data-testid="input-setup-message"
              className="flex-1 min-h-[40px] max-h-40 resize-none overflow-y-auto whitespace-pre-wrap break-words"
            />
            <IconButton type="submit" label="Send message" disabled={pending || (!input.trim() && attachments.length === 0) || input.trim().length > MAX_MESSAGE_CHARS} data-testid="button-send-setup">
              <Send className="w-4 h-4" aria-hidden="true" />
            </IconButton>
          </div>
          {input.length > MAX_MESSAGE_CHARS * 0.8 && (
            <p
              className={`text-[11px] text-right ${input.trim().length > MAX_MESSAGE_CHARS ? "text-destructive font-medium" : "text-muted-foreground"}`}
              data-testid="text-char-count"
            >
              {input.trim().length.toLocaleString()} / {MAX_MESSAGE_CHARS.toLocaleString()}
              {input.trim().length > MAX_MESSAGE_CHARS && " — too long to send"}
            </p>
          )}
        </form>
      </Card>

      <p className="text-xs text-muted-foreground flex items-center gap-1.5">
        <Bot className="w-3.5 h-3.5" />
        The agent never makes changes without your explicit approval. Every action is audit-logged.
      </p>
    </div>
  );
}
